/**
 * getTyped — Pillar 3 typed single-record reader for non-view-proxy SL
 * endpoints (e.g. `GET /v1/agents`, `GET /v1/missions/<id>`).
 *
 * Wraps the existing `get` (which returns `ServiceFetchResult<T>` without
 * shape validation) with a zod `safeParse` against the per-surface wire
 * response schema. On parse failure throws `ContractMismatchError`
 * populated with `{slug, expectedShape, actualShape, rawData}`.
 *
 * D-33 R1: NEVER returns on safeParse failure. NEVER swallows. Always
 * throws.
 *
 * Per ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2 Pillar 3.
 *
 * Usage:
 *   const envelope = await getTyped(
 *     '/v1/agents',
 *     AgentsEnvelopeSchema,
 *     { signal },
 *   )
 */

import type { z } from 'zod'
import { get } from './serviceLayerClient'
import { ContractMismatchError } from './slErrors'

export interface GetTypedOptions {
  signal?: AbortSignal
  timeoutMs?: number
}

/**
 * Typed single-record reader. The caller supplies a zod schema for the
 * full response body; this primitive returns the parsed object.
 *
 * `path` doubles as the slug for ContractMismatchError forensics — when
 * the caller passes `'/v1/agents'`, that string surfaces in the error.
 *
 * @throws `ContractMismatchError` on safeParse failure (D-33 R1).
 */
export async function getTyped<T>(
  path: string,
  responseSchema: z.ZodType<T>,
  opts: GetTypedOptions = {},
): Promise<T> {
  const res = await get<unknown>(path, {
    signal: opts.signal,
    timeoutMs: opts.timeoutMs,
  })

  const parsed = responseSchema.safeParse(res.data)
  if (!parsed.success) {
    throw new ContractMismatchError({
      slug: path,
      expectedShape: 'getTyped<' + path + '>',
      actualShape: summarizeActualShape(res.data),
      rawData: res.data,
      message:
        'Contract mismatch on SL path "'
        + path
        + '": '
        + parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; '),
    })
  }

  return parsed.data
}

function summarizeActualShape(value: unknown): string {
  if (value === null) return 'null'
  if (typeof value !== 'object') return typeof value
  if (Array.isArray(value)) return 'array(' + value.length + ')'
  const keys = Object.keys(value as Record<string, unknown>)
  return 'object{' + keys.slice(0, 6).join(',') + (keys.length > 6 ? ',…' : '') + '}'
}
